import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Heart,
    HeartHandshake,
    HeartPulse,
    BookOpen,
    ShieldCheck,
    Users,
    Megaphone,
    Activity,
    CheckCircle2,
    ExternalLink,
} from "lucide-react";
import Seo from "../lib/seo";
import Reveal from "../components/Reveal";
import { SOCIAL } from "../lib/constants";

const GIVEBUTTER_URL = SOCIAL.givebutter || "https://givebutter.com/turning-loss-into-change-2rfwrb";
const GIVEBUTTER_EMBED_URL = "https://givebutter.com/embed/c/turning-loss-into-change-2rfwrb";

const IMPACT_CARDS = [
    {
        icon: BookOpen,
        title: "Save Lives Through Education",
        desc: "Your donation helps provide CPR training and education so more people know how to act in a life-threatening emergency.",
        accent: "coral",
    },
    {
        icon: HeartPulse,
        title: "Promote AED Awareness & Access",
        desc: "We advocate for more AEDs in public spaces and teach communities about their importance and use.",
        accent: "navy",
    },
    {
        icon: ShieldCheck,
        title: "Support Emergency Preparedness",
        desc: "We provide resources and programs that help families, businesses, and communities prepare for emergencies before they happen.",
        accent: "coral",
    },
    {
        icon: Users,
        title: "Strengthen Our Communities",
        desc: "Together, we build stronger, more aware communities that look out for one another and respond with confidence.",
        accent: "navy",
    },
    {
        icon: Megaphone,
        title: "Advocate for Public Safety",
        desc: "We raise awareness and advocate for policies, training, and resources that improve public safety for everyone.",
        accent: "coral",
    },
    {
        icon: HeartHandshake,
        title: "Support Families and Healing",
        desc: "We connect families to resources, support, and hope as they heal and honor their loved ones' legacy.",
        accent: "navy",
    },
];

const DONATION_USES = [
    "CPR education and training initiatives",
    "Emergency preparedness resources and programs",
    "AED awareness and accessibility efforts",
    "Community outreach and awareness events",
    "Public safety advocacy and education",
    "Support resources for families and survivors",
    "Educational materials and life-saving tools",
];

export default function DonatePage() {
    // Load Givebutter widget script once
    useEffect(() => {
        const scriptId = "givebutter-widget-script";
        if (!document.getElementById(scriptId)) {
            const s = document.createElement("script");
            s.id = scriptId;
            s.src = "https://widgets.givebutter.com/latest.umd.cjs?acct=2rfwrb&p=other";
            s.async = true;
            document.body.appendChild(s);
        }
    }, []);

    const scrollToDonate = () => {
        const el = document.getElementById("givebutter-embed");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <>
            <Seo
                title="Donate to JC Law Foundation | Registered 501(c)(3) Nonprofit"
                description="Support JC Law Foundation's charitable mission to improve cardiac emergency preparedness, expand AED awareness, and advocate for lifesaving safety measures in hotels."
                path="/donate"
            />

            {/* BANNER IMAGE — Why Your Donation Makes A Difference */}
            <section className="bg-navy-900 relative overflow-hidden" data-testid="donate-banner">
                <img
                    src="/images/donation-banner.jpg"
                    alt="Why your donation makes a difference — JC Law Foundation"
                    className="w-full h-auto block"
                    loading="eager"
                />
            </section>

            {/* GIVEBUTTER DONATION EMBED — FIRST THING VISITORS SEE */}
            <section className="bg-navy-900 relative overflow-hidden pt-12 md:pt-16 pb-16 md:pb-20" id="givebutter-embed" data-testid="donate-embed-section">
                <div className="absolute inset-0 bg-noise opacity-25 pointer-events-none" />
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 relative">
                    <Reveal className="text-center mb-8 md:mb-10">
                        <p className="overline mb-4 text-coral">Secure Donation · Powered by Givebutter</p>
                        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-editorial leading-tight mb-4">
                            Turning Loss <span className="text-coral italic">Into Change</span>
                        </h1>
                        <div className="flex items-center justify-center gap-3 mb-5">
                            <span className="h-px w-12 bg-coral" />
                            <Heart className="w-4 h-4 text-coral fill-coral" />
                            <span className="h-px w-12 bg-coral" />
                        </div>
                        <p className="text-editorial/85 max-w-2xl mx-auto text-base md:text-lg">
                            Your support helps promote CPR education, AED access, and emergency preparedness — so more people are ready when every second matters.
                        </p>
                        <p className="mt-5 text-editorial/75 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                            JC Law Foundation is a registered <strong className="text-editorial">501(c)(3) nonprofit organization</strong>. Your contribution supports our charitable mission to improve cardiac emergency preparedness, expand awareness, and advocate for lifesaving protections in hotels.
                        </p>
                    </Reveal>

                    <Reveal delay={0.05}>
                        <div className="bg-white shadow-2xl border border-editorial-border overflow-hidden">
                            <iframe
                                src={GIVEBUTTER_EMBED_URL}
                                title="JC Law Foundation — Donate on Givebutter"
                                className="w-full"
                                style={{ minHeight: "820px", border: "0" }}
                                allow="payment *; clipboard-write"
                                loading="eager"
                                data-testid="givebutter-iframe"
                            />
                        </div>
                        <p className="mt-3 text-center text-xs uppercase tracking-[0.22em] text-gold" data-testid="donate-trust-line">
                            Registered 501(c)(3) Nonprofit Organization
                        </p>
                        <p className="mt-3 text-center text-sm text-editorial/75">
                            Trouble viewing the form?{" "}
                            <a
                                href={GIVEBUTTER_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-coral hover:text-coral-dark font-medium underline"
                                data-testid="givebutter-fallback-link"
                            >
                                Donate on Givebutter <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* YOUR GIFT. REAL IMPACT. */}
            <section className="bg-editorial py-20 md:py-24" data-testid="donate-real-impact">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <Reveal className="text-center max-w-3xl mx-auto mb-14">
                        <div className="flex items-center justify-center gap-4 mb-5">
                            <Heart className="w-5 h-5 text-coral fill-coral" />
                            <h2 className="font-serif text-3xl md:text-5xl text-navy-900 leading-tight tracking-tight">
                                YOUR GIFT. REAL IMPACT.
                            </h2>
                            <Heart className="w-5 h-5 text-coral fill-coral" />
                        </div>
                        <div className="flex items-center justify-center gap-2 mb-6">
                            <span className="h-px w-16 bg-coral" />
                            <span className="h-px w-16 bg-navy-900" />
                        </div>
                        <p className="text-navy-800/80 text-lg leading-relaxed">
                            Every donation helps build a safer, more prepared community and empowers individuals to take action that can save lives.
                        </p>
                    </Reveal>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-6">
                        {IMPACT_CARDS.map((card, i) => {
                            const Icon = card.icon;
                            const isCoral = card.accent === "coral";
                            return (
                                <Reveal key={i} delay={i * 0.05}>
                                    <div className="text-center group">
                                        <div
                                            className={`w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center transition-transform group-hover:scale-110 ${
                                                isCoral ? "bg-coral" : "bg-navy-900"
                                            }`}
                                        >
                                            <Icon className="w-9 h-9 text-white" strokeWidth={1.8} />
                                        </div>
                                        <h3
                                            className={`font-sans text-[11px] md:text-xs uppercase tracking-[0.15em] font-bold leading-tight mb-3 ${
                                                isCoral ? "text-coral" : "text-navy-900"
                                            }`}
                                        >
                                            {card.title}
                                        </h3>
                                        <p className="text-navy-800/75 text-xs md:text-sm leading-relaxed">
                                            {card.desc}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* A LEGACY OF PURPOSE */}
            <section className="bg-navy-900 text-editorial relative overflow-hidden" data-testid="donate-legacy">
                <div className="absolute inset-0 bg-noise opacity-25 pointer-events-none" />
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-24 grid lg:grid-cols-12 gap-12 items-center relative">
                    <Reveal className="lg:col-span-6">
                        <h2 className="font-serif italic text-4xl md:text-6xl text-coral leading-tight mb-6">
                            A Legacy of Purpose
                        </h2>
                        <div className="flex items-center gap-3 mb-7">
                            <span className="h-px w-10 bg-coral" />
                            <Heart className="w-4 h-4 text-coral fill-coral" />
                            <span className="h-px w-10 bg-coral" />
                        </div>
                        <p className="text-editorial/85 text-lg leading-relaxed mb-5 max-w-xl">
                            JC's life was a reminder that anything can happen in a moment. Through this foundation, his legacy lives on by educating, preparing, and empowering others.
                        </p>
                        <p className="font-serif text-xl md:text-2xl text-editorial leading-snug max-w-xl">
                            Your donation helps turn heartbreak into hope for countless others.
                        </p>
                    </Reveal>

                    <Reveal delay={0.1} className="lg:col-span-6">
                        <div className="relative -rotate-2 hover:rotate-0 transition-transform duration-700">
                            <div className="bg-white p-3 shadow-2xl">
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img
                                        src="/images/jc-real.png"
                                        alt="JC — A Legacy of Purpose"
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                            </div>
                            <div className="absolute -bottom-6 -right-2 md:-right-6">
                                <Heart className="w-16 h-16 text-coral fill-coral drop-shadow-2xl" />
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* WHERE YOUR DONATION GOES + POWER OF PREPAREDNESS */}
            <section className="bg-editorial py-20 md:py-24" data-testid="donate-where">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
                    <Reveal className="lg:col-span-5">
                        <h2 className="font-serif text-3xl md:text-4xl text-navy-900 leading-tight mb-3 uppercase tracking-wide">
                            Where Your Donation Goes
                        </h2>
                        <div className="h-1 w-16 bg-coral mb-7" />
                        <ul className="space-y-3">
                            {DONATION_USES.map((use, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <Heart className="w-4 h-4 mt-1 text-coral fill-coral flex-shrink-0" />
                                    <span className="text-navy-800/85 text-base leading-relaxed">{use}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>

                    <Reveal delay={0.1} className="lg:col-span-3 flex flex-col items-center justify-center text-center">
                        <Activity className="w-24 h-24 text-coral mb-4" strokeWidth={1.6} />
                        <p className="font-sans uppercase tracking-[0.18em] text-sm font-bold text-navy-900 leading-snug">
                            Together, we can be the difference between tragedy and a second chance.
                        </p>
                    </Reveal>

                    <Reveal delay={0.15} className="lg:col-span-4">
                        <div className="bg-editorial-muted border border-editorial-border p-8 md:p-10 h-full flex flex-col justify-center">
                            <h3 className="font-sans uppercase tracking-[0.15em] text-sm font-bold text-navy-900 text-center mb-3">
                                The Power of Preparedness
                            </h3>
                            <div className="flex items-center justify-center mb-5">
                                <span className="h-px w-10 bg-coral" />
                                <span className="text-coral mx-3 font-serif text-3xl leading-none">"</span>
                                <span className="h-px w-10 bg-coral" />
                            </div>
                            <p className="text-navy-800/85 text-base md:text-lg leading-relaxed text-center mb-5 italic">
                                Immediate action can double or even triple a person's chance of survival after cardiac arrest.
                            </p>
                            <p className="font-sans uppercase tracking-[0.12em] text-sm font-bold text-coral text-center">
                                Your support helps make that possible.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* MAKE AN IMPACT TODAY CTA */}
            <section className="bg-navy-900 text-editorial relative overflow-hidden" data-testid="donate-final-cta">
                <div className="absolute inset-0 bg-noise opacity-25 pointer-events-none" />
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 md:py-16 grid md:grid-cols-12 gap-8 items-center relative">
                    <div className="md:col-span-1 flex justify-center md:justify-start">
                        <div className="w-16 h-16 rounded-full bg-coral flex items-center justify-center">
                            <HeartPulse className="w-8 h-8 text-white" />
                        </div>
                    </div>
                    <div className="md:col-span-6">
                        <h2 className="font-sans uppercase tracking-[0.15em] text-xl md:text-2xl font-bold mb-2">
                            Make an Impact Today
                        </h2>
                        <p className="text-editorial/80 text-base md:text-lg leading-relaxed">
                            Your donation helps save lives, strengthen communities, and honor JC's legacy in the most meaningful way.
                        </p>
                    </div>
                    <div className="md:col-span-5 flex flex-col items-stretch md:items-end gap-3">
                        <a
                            href={GIVEBUTTER_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="final-donate-cta"
                            className="inline-flex items-center justify-center gap-3 bg-coral hover:bg-coral-dark text-white font-semibold px-10 py-4 transition-colors w-full md:w-auto"
                        >
                            <Heart className="w-5 h-5 fill-white" />
                            DONATE NOW
                        </a>
                        <p className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-gold text-right">
                            Registered 501(c)(3) Nonprofit Organization
                        </p>
                        <p className="font-serif italic text-editorial/75 text-right text-sm md:text-base">
                            Thank you for helping us save more lives.
                        </p>
                    </div>
                </div>
            </section>

            {/* Sponsor & partnership */}
            <section className="bg-editorial py-16">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
                    <div className="bg-editorial-muted border border-editorial-border p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div>
                            <p className="overline text-coral mb-2">Sponsor & Partnerships</p>
                            <p className="text-navy-800/85 max-w-2xl">
                                We welcome partnerships with businesses, organizations, and community leaders who share our mission of CPR education and emergency preparedness.
                            </p>
                        </div>
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 text-sm font-medium text-navy-900 border-b border-navy-900 hover:border-coral hover:text-coral pb-1 transition-colors flex-shrink-0"
                            data-testid="partnership-link"
                        >
                            Explore Partnership
                            <CheckCircle2 className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
